import { Link } from 'react-router-dom';
import LandingNav from '../components/LandingNav';
import Footer from '../components/Footer';
import PageSEO from '../components/PageSEO';

const BASE_URL = 'https://momentsent.com';
const PATH = '/what-to-write-in-a-christmas-card';

/**
 * Message-ideas guide: /what-to-write-in-a-christmas-card
 *
 * A genuinely useful, citable resource (GEO play): answer-first intro, then
 * dozens of ready-to-use Christmas messages grouped by tone and relationship.
 * Cross-links to /christmas-cards and /signup. Emits Article + FAQPage +
 * BreadcrumbList JSON-LD.
 */

interface MessageGroup {
  heading: string;
  intro:   string;
  messages: string[];
}

const GROUPS: MessageGroup[] = [
  {
    heading: 'Warm and heartfelt',
    intro:   'When you want the card to carry real warmth, speak to what the season and the person mean to you.',
    messages: [
      'Wishing you a Christmas full of warmth, laughter, and the people you love most. Merry Christmas.',
      'May your home be filled with joy this season and your year ahead with every good thing. Merry Christmas.',
      'Thinking of you this Christmas and feeling grateful to have you in my life. Sending you so much love.',
      'May the magic of Christmas fill your heart and brighten every day of the season. Merry Christmas.',
      'Here is to a season of cozy nights, good food, and time with the ones who matter. Merry Christmas.',
      'Sending you warm wishes and a big hug this Christmas. May it be everything you hope for.',
    ],
  },
  {
    heading: 'Short and simple',
    intro:   'A few warm words are plenty for a holiday card. These work for almost anyone.',
    messages: [
      'Merry Christmas and a happy New Year!',
      'Wishing you peace, joy, and a wonderful holiday season.',
      'Happy holidays! Hope your season is merry and bright.',
      'Warmest wishes for a Christmas full of joy.',
      'Sending you love and good cheer this holiday season.',
      'May your holidays be merry and your New Year bright.',
    ],
  },
  {
    heading: 'Funny and lighthearted',
    intro:   'A little humor makes a card memorable. Keep it kind and keep it festive.',
    messages: [
      'Merry Christmas! May your wifi be strong and your relatives be merry.',
      'Wishing you a Christmas as sweet as the cookies you will absolutely eat too many of.',
      'Happy holidays! Remember, the calories in Christmas cookies do not count. It is the rules.',
      'Merry Christmas! Hoping your stockings are full and your in-laws are tolerable.',
      'Tis the season to be jolly, or at least to fake it until the eggnog kicks in. Merry Christmas!',
      'Happy holidays! May your tree be merry and your batteries be included.',
    ],
  },
  {
    heading: 'For family',
    intro:   'Holiday cards to family are a chance to say what you do not always stop to say.',
    messages: [
      'Merry Christmas to the family I am luckiest to have. I love you all.',
      'However far apart we are, you are always close to my heart at Christmas. Sending all my love.',
      'Grateful for this family and every memory we make. Merry Christmas.',
      'Wishing our family a Christmas full of love, laughter, and a little chaos, just how we like it.',
      'Home is wherever you all are. Merry Christmas, with love.',
    ],
  },
  {
    heading: 'For friends',
    intro:   'Celebrate the friendship along with the season.',
    messages: [
      'Merry Christmas to a friend who feels like family. So glad to have you in my life.',
      'Here is to another year of friendship and a holiday season full of good times. Merry Christmas!',
      'Friends like you make the season brighter. Wishing you a wonderful Christmas.',
      'Thank you for being such a good friend all year. Merry Christmas and a happy New Year.',
      'Cheers to you this Christmas. May your season be merry and your glass be full.',
    ],
  },
  {
    heading: 'For clients and colleagues',
    intro:   'Keep professional holiday cards warm, brief, and appreciative.',
    messages: [
      'Wishing you and your team a joyful holiday season and a prosperous New Year.',
      'Thank you for a great year of working together. Happy holidays from all of us.',
      'Season’s greetings and best wishes for the year ahead. It is a pleasure to work with you.',
      'Warmest wishes this holiday season, and our sincere thanks for your continued trust.',
      'Happy holidays! We look forward to another wonderful year together.',
    ],
  },
];

const FAQ = [
  {
    q: 'What should I write in a Christmas card?',
    a: 'Open with a holiday greeting like "Merry Christmas" or "Happy holidays," add one warm, personal line about the person or the year, and close with a wish for the season and the New Year, then your name. A short, sincere message means more than a long, generic one. If you are stuck, pick a message from this guide that matches your tone and relationship and change a word or two to make it yours.',
  },
  {
    q: 'What is a good short Christmas message?',
    a: 'A good short Christmas message is warm and simple, for example: "Wishing you peace, joy, and a wonderful holiday season." or "Merry Christmas and a happy New Year!" Short messages work well for a long card list or when you want something classic and sincere.',
  },
  {
    q: 'What can I write in a Christmas card for business or clients?',
    a: 'Keep it warm, brief, and appreciative, for example: "Wishing you and your team a joyful holiday season and a prosperous New Year. Thank you for a great year of working together." Avoid anything too personal and close with your name or company name.',
  },
  {
    q: 'How do I sign off a Christmas card?',
    a: 'Match the sign-off to your relationship. "Love," works for family and close friends, "With love" for a partner, and "Warm wishes," "Season’s greetings," or "Best," for friends, clients, and colleagues. Then add your name.',
  },
];

export default function WhatToWriteChristmasPage() {
  const canonical = `${BASE_URL}${PATH}`;

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'What to Write in a Christmas Card: 30+ Message Ideas',
      description: 'Ready-to-use Christmas card messages grouped by tone and relationship, from warm and heartfelt to funny, plus tips on what to write and how to sign off.',
      author: { '@type': 'Organization', name: 'MomentSent' },
      publisher: {
        '@type': 'Organization',
        name: 'MomentSent',
        logo: { '@type': 'ImageObject', url: `${BASE_URL}/logo-wordmark.png` },
      },
      mainEntityOfPage: canonical,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
        { '@type': 'ListItem', position: 2, name: 'What to Write in a Christmas Card', item: canonical },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <PageSEO
        title="What to Write in a Christmas Card: 30+ Message Ideas | MomentSent"
        description="Stuck on what to write in a Christmas card? Use these ready-to-go holiday messages grouped by tone and relationship, from heartfelt to funny, plus tips on what to say and how to sign off."
        canonical={canonical}
        structuredData={structuredData}
      />
      <LandingNav />

      {/* ── Hero / answer-first intro ───────────────────────────────────────── */}
      <section className="pt-28 pb-12 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-body text-sm text-sage font-semibold tracking-widest uppercase mb-4">
            Message ideas
          </p>
          <h1 className="font-heading text-4xl md:text-5xl text-navy leading-tight mb-6">
            What to write in a Christmas card
          </h1>
          <p className="font-body text-lg text-navy/80 leading-relaxed">
            The best Christmas card messages are warm, brief, and personal. Start with a holiday
            greeting, add one specific line about the person or the year, and close with a wish for
            the season and the New Year. Below are more than 30 ready-to-use messages grouped by tone
            and relationship. Pick one that fits, change a word or two to make it yours, and you are
            done.
          </p>
        </div>
      </section>

      {/* ── Message groups ──────────────────────────────────────────────────── */}
      <section className="pb-4 px-6">
        <div className="max-w-3xl mx-auto space-y-10">
          {GROUPS.map((g) => (
            <div key={g.heading}>
              <h2 className="font-heading text-2xl text-navy mb-2">{g.heading}</h2>
              <p className="font-body text-base text-gray-500 leading-relaxed mb-5">{g.intro}</p>
              <ul className="space-y-3">
                {g.messages.map((m) => (
                  <li
                    key={m}
                    className="bg-white rounded-card p-5 shadow-warm border border-gray-100
                               font-body text-base text-gray-700 leading-relaxed"
                  >
                    {m}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* ── Tips on writing the card ────────────────────────────────────────── */}
      <section className="py-10 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl text-navy mb-6">Tips for writing a Christmas card</h2>
          <ul className="space-y-4">
            {[
              'Open with a clear greeting. "Merry Christmas" or "Happy holidays" sets the tone right away and works for almost everyone.',
              'Add one specific detail. A line about a shared memory, the year you have had, or your hopes for theirs makes the card feel personal.',
              'Match the tone to the person. Heartfelt for family, playful for close friends, warm and brief for clients and colleagues.',
              'Mind the season for those who do not celebrate Christmas. "Happy holidays" or "Season’s greetings" keeps it inclusive.',
              'Sign off with the right closer. "Love" for family, "Warm wishes" or "Season’s greetings" for friends and colleagues, then your name.',
            ].map((t) => (
              <li key={t} className="flex gap-3">
                <span className="text-sage font-bold mt-0.5" aria-hidden="true">✓</span>
                <span className="font-body text-base text-gray-600 leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────────────────── */}
      <section className="py-10 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl text-navy mb-6">Frequently asked questions</h2>
          <div className="space-y-6">
            {FAQ.map((f) => (
              <div key={f.q}>
                <h3 className="font-heading text-lg text-navy mb-2">{f.q}</h3>
                <p className="font-body text-base text-gray-600 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA back to the Christmas product ───────────────────────────────── */}
      <section className="py-20 px-6 bg-sage/10 mt-auto">
        <div className="max-w-xl mx-auto text-center">
          <h2 className="font-heading text-3xl text-navy mb-4">
            Found the words? Let us mail the whole list.
          </h2>
          <p className="font-body text-base text-gray-500 leading-relaxed mb-8">
            With <Link to="/christmas-cards" className="text-sage font-semibold hover:underline">automatic Christmas cards</Link> from
            MomentSent, you add your list once and we print, address, and mail a real card to each
            person, with postage included, so they arrive in time for the holidays.
          </p>
          <Link to="/signup" className="btn-primary inline-block px-8 py-3 text-base">
            Start your free trial →
          </Link>
          <p className="mt-3 font-body text-xs text-gray-400">
            14-day free trial. No credit card required.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
